import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEO from '@/components/base/SEO';
import { stats, testimonials } from '@/mocks/comics';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [coopFormStatus, setCoopFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [coopFormError, setCoopFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('success');
    setFormError('');
    form.reset();
  };

  const handleCoopSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setCoopFormStatus('success');
      form.reset();
      return;
    }

    setCoopFormStatus('success');
    setCoopFormError('');
    form.reset();
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩国漫画网',
    description: '韩国漫画网是专业的韩漫大全平台，提供海量免费韩国漫画在线阅读。',
    about: {
      '@type': 'Organization',
      name: '韩国漫画网',
      alternateName: '韩漫大全',
      url: 'http://laimim.com/',
      logo: 'http://laimim.com/logo.png',
      foundingDate: '2020',
      sameAs: ['http://laimim.com/'],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'hello@laimim.com',
        availableLanguage: ['Chinese'],
      },
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />
      <SEO
        title="关于我们 - 韩国漫画网 — 品牌介绍与联系方式"
        description="了解韩国漫画网品牌故事，专业的韩国免费漫画在线阅读平台，海量资源每日更新。查看联系方式与用户口碑。"
        keywords="关于韩国漫画网,韩漫平台,漫画网站,联系我们"
        canonicalUrl="http://laimim.com/about"
      />
      <Navbar />

      <main className="min-h-screen pt-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="py-12 md:py-16">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight">
                关于韩国漫画网
              </h1>
              <p className="text-base md:text-lg text-foreground-600 mt-4 leading-relaxed">
                韩国漫画网（韩漫大全）成立于2020年，致力于为全球华语用户提供最优质的韩国漫画免费在线阅读服务。
              </p>
            </div>
          </section>

          <section className="pb-12 md:pb-16">
            <div className="flex flex-col lg:flex-row gap-10 md:gap-16 items-center">
              <div className="w-full lg:w-1/2">
                <div className="rounded-3xl overflow-hidden bg-background-200 h-[280px] md:h-[400px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20studio%20with%20large%20monitors%20displaying%20colorful%20digital%20comic%20art%2C%20team%20collaborating%20on%20webtoon%20projects%2C%20warm%20cozy%20workspace%2C%20indoor%20plants%2C%20natural%20light%2C%20creative%20atmosphere%2C%20professional%20yet%20relaxed%20environment%2C%20soft%20neutral%20colors&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="韩国漫画网团队"
                    title="韩国漫画网 - 团队工作场景"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="w-full lg:w-1/2">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  品牌故事
                </h2>
                <p className="text-sm md:text-base text-foreground-600 mt-5 leading-relaxed">
                  韩国漫画网是由一群热爱韩国漫画的年轻人创立的平台。我们发现很多韩漫爱好者苦于找不到高质量的免费阅读渠道，于是决定打造一个专业的韩漫大全网站。
                </p>
                <p className="text-sm md:text-base text-foreground-600 mt-3 leading-relaxed">
                  从最初的几百部漫画起步，到如今拥有超过 <strong className="text-foreground-900">5000部</strong> 精选中韩双语漫画资源，覆盖恋爱、热血、奇幻、搞笑等 <strong className="text-foreground-900">九大题材</strong>，我们始终坚持 <strong className="text-foreground-900">免费阅读</strong> 和 <strong className="text-foreground-900">每日更新</strong> 的承诺。
                </p>
                <p className="text-sm md:text-base text-foreground-600 mt-3 leading-relaxed">
                  我们自主研发的 <strong className="text-foreground-900">下拉式阅读</strong> 技术，让读者可以像刷社交媒体一样流畅地阅读漫画，告别繁琐的翻页操作，沉浸在连续的视觉叙事中。
                </p>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 bg-foreground-950 -mx-4 md:-mx-6 px-4 md:px-6 rounded-3xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-3xl md:text-5xl font-heading font-bold text-background-50">
                    {stat.value}
                  </p>
                  <p className="text-xs md:text-sm text-foreground-400 mt-2">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="py-12 md:py-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                我们的价值观
              </h2>
              <p className="text-sm md:text-base text-foreground-600 mt-2">
                用心做好每一件事，为读者创造价值
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: 'ri-heart-line', title: '免费阅读', desc: '所有漫画内容永久免费，不设付费墙，让每位读者都能畅快阅读' },
                { icon: 'ri-timer-flash-line', title: '每日更新', desc: '热门连载每日同步更新，第一时间送上最新章节，追更不等待' },
                { icon: 'ri-file-list-line', title: '下拉式阅读', desc: '独创的下拉式阅读模式，丝滑流畅的阅读体验，沉浸感满分' },
                { icon: 'ri-shield-check-line', title: '高清画质', desc: '所有漫画采用高清扫描与处理，确保每一格画面都清晰锐利' },
                { icon: 'ri-global-line', title: '全终端适配', desc: 'PC、平板、手机全平台无缝适配，随时随地想看就看' },
                { icon: 'ri-user-voice-line', title: '读者至上', desc: '倾听每一位读者的声音，不断优化功能和内容，做最懂你的漫画平台' },
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-6 rounded-2xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-colors"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-4">
                    <i className={`${item.icon} text-xl`} />
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900">{item.title}</h3>
                  <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="py-12 md:py-16 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                读者口碑
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mt-4">
                用户评价
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm" />
                    ))}
                  </div>
                  <blockquote className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <div className="flex items-center gap-3 mt-5 pt-5 border-t border-background-200">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-300 flex-shrink-0">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="cooperation" className="py-12 md:py-16">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                商务合作
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mt-4">
                平台数据与商务合作
              </h2>
              <p className="text-sm md:text-base text-foreground-600 mt-3 max-w-2xl mx-auto leading-relaxed">
                韩国漫画网日活用户超过50万，月阅读量突破2亿次，是国内领先的韩漫阅读平台。我们诚邀漫画版权方、翻译团队、广告主等合作伙伴携手共赢。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 max-w-4xl mx-auto">
              {[
                { icon: 'ri-copyright-line', title: '版权合作', desc: '正版漫画授权引入，提供完整的版权保护与收益分成方案' },
                { icon: 'ri-translate-2', title: '翻译合作', desc: '招募优质翻译团队，共同打造高品质双语韩漫阅读体验' },
                { icon: 'ri-advertisement-line', title: '广告投放', desc: '精准触达年轻漫画用户群体，提供品牌曝光与效果广告位' },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-background-100 border border-background-200/70 text-center"
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className={`${item.icon} text-2xl`} />
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="max-w-2xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                {[
                  { icon: 'ri-mail-line', label: '商务邮箱', value: 'hello@laimim.com' },
                  { icon: 'ri-phone-line', label: '联系电话', value: '+82 2-1234-5678' },
                  { icon: 'ri-map-pin-line', label: '办公地址', value: '首尔市江南区德黑兰路123号' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-100 border border-background-200/70"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                      <i className={`${item.icon} text-lg`} />
                    </div>
                    <p className="text-xs text-foreground-500">{item.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{item.value}</p>
                  </div>
                ))}
              </div>

              <form
                onSubmit={handleCoopSubmit}
                className="flex flex-col gap-4 p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="coop-name" className="block text-sm font-medium text-foreground-700 mb-1.5">联系人姓名</label>
                    <input
                      type="text"
                      id="coop-name"
                      name="name"
                      required
                      className="w-full h-11 px-4 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入联系人姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="coop-email" className="block text-sm font-medium text-foreground-700 mb-1.5">联系邮箱</label>
                    <input
                      type="email"
                      id="coop-email"
                      name="email"
                      required
                      className="w-full h-11 px-4 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入联系邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="coop-type" className="block text-sm font-medium text-foreground-700 mb-1.5">合作类型</label>
                  <select
                    id="coop-type"
                    name="coop_type"
                    required
                    className="w-full h-11 px-4 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                  >
                    <option value="">请选择合作类型</option>
                    <option value="copyright">版权合作</option>
                    <option value="translation">翻译合作</option>
                    <option value="advertising">广告投放</option>
                    <option value="other">其他合作</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="coop-message" className="block text-sm font-medium text-foreground-700 mb-1.5">合作详情</label>
                  <textarea
                    id="coop-message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请简要描述您的合作意向与需求（最多500字）"
                  />
                </div>

                <input
                  type="text"
                  name="company_alt"
                  className="spam-guard"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  className="w-full h-11 bg-accent-500 text-background-50 font-medium text-sm rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  提交合作意向
                </button>

                {coopFormStatus === 'success' && (
                  <p className="text-sm text-center text-green-600 font-medium">合作意向提交成功！商务团队会在3个工作日内与您联系。</p>
                )}
                {coopFormStatus === 'error' && (
                  <p className="text-sm text-center text-red-500 font-medium">{coopFormError}</p>
                )}
              </form>
            </div>
          </section>

          <section id="contact" className="py-12 md:py-16 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                  联系我们
                </h2>
                <p className="text-sm md:text-base text-foreground-600 mt-2">
                  有任何问题或建议？欢迎随时与我们联系
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                {[
                  { icon: 'ri-mail-line', label: '电子邮箱', value: 'hello@laimim.com' },
                  { icon: 'ri-customer-service-line', label: '客服标识', value: 'laimim' },
                  { icon: 'ri-map-pin-line', label: '公司地址', value: '首尔特别市江南区' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-50 border border-background-200/70"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                      <i className={`${item.icon} text-lg`} />
                    </div>
                    <p className="text-xs text-foreground-500">{item.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{item.value}</p>
                  </div>
                ))}
              </div>

              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4 p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full h-11 px-4 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full h-11 px-4 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full h-11 px-4 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  />
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  className="spam-guard"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  className="w-full h-11 bg-primary-500 text-background-50 font-medium text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  发送留言
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-center text-green-600 font-medium">留言发送成功！我们会尽快回复您。</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-center text-red-500 font-medium">{formError}</p>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}
